import type { DocumentDto } from '@app/contracts';
import { Link } from '@tanstack/react-router';
import {
  ClipboardList,
  FileText,
  FileWarning,
  Globe,
  Layers,
  ScrollText,
  Settings2,
  Shield,
  Trash2,
} from 'lucide-react';
import { documentViewSearch } from '../../../router';
import { DocumentRightsButton } from './DocumentRightsButton.js';

const EXTENSION_CONFIG: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
  swot: { label: 'SWOT', icon: <Layers className="h-4 w-4" />, color: 'bg-blue-100 text-blue-700' },
  pestel: {
    label: 'PESTEL',
    icon: <Globe className="h-4 w-4" />,
    color: 'bg-green-100 text-green-700',
  },
  needsWait: {
    label: 'Parties intéressées',
    icon: <FileText className="h-4 w-4" />,
    color: 'bg-orange-100 text-orange-700',
  },
  paOperational: {
    label: 'PA Opérationnel',
    icon: <Settings2 className="h-4 w-4" />,
    color: 'bg-purple-100 text-purple-700',
  },
  auditPlanning: {
    label: "Plan d'audit",
    icon: <ClipboardList className="h-4 w-4" />,
    color: 'bg-slate-100 text-slate-700',
  },
  auditReport: {
    label: "Rapport d'audit",
    icon: <ScrollText className="h-4 w-4" />,
    color: 'bg-indigo-100 text-indigo-700',
  },
  nonConformity: {
    label: 'Non-conformité',
    icon: <FileWarning className="h-4 w-4" />,
    color: 'bg-red-100 text-red-700',
  },
  managementReview: {
    label: 'Revue de direction',
    icon: <Shield className="h-4 w-4" />,
    color: 'bg-amber-100 text-amber-700',
  },
};

interface Props {
  document: DocumentDto;
  onDelete?: (id: string) => void;
}

export function DocumentCard({ document, onDelete }: Props) {
  const cfg = EXTENSION_CONFIG[document.extension] ?? {
    label: document.extension.toUpperCase(),
    icon: <FileText className="h-4 w-4" />,
    color: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="group relative flex flex-col justify-between rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div>
        <div className="mb-3 flex items-center justify-between">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${cfg.color}`}
          >
            {cfg.icon}
            {cfg.label}
          </span>
          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(document.id)}
              className="opacity-0 group-hover:opacity-100 flex h-7 w-7 items-center justify-center rounded-lg text-slate-300 hover:bg-red-50 hover:text-red-500 transition-all"
              title="Supprimer"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
        <h3 className="mb-1 font-semibold text-slate-900 line-clamp-2">{document.name}</h3>
        <p className="text-xs text-slate-400">
          Modifié le {new Date(document.updatedAt).toLocaleDateString('fr-FR')}
        </p>
      </div>
      <div className="mt-4 flex flex-col gap-2">
        <DocumentRightsButton
          document={document}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-center text-sm font-medium text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
        />
        <Link
          to="/documents/$id"
          params={{ id: document.id }}
          search={documentViewSearch.closed}
          className="block rounded-xl bg-slate-50 px-4 py-2 text-center text-sm font-medium text-slate-600 transition-colors hover:bg-emerald-50 hover:text-emerald-700"
        >
          Ouvrir
        </Link>
      </div>
    </div>
  );
}
